import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["item", "trigger", "content", "icon"];
  static values = {
    allowMultiple: { type: Boolean, default: false }, // Allow multiple items to be open at once
    storageKey: { type: String, default: "accordion_state" }, // Key for sessionStorage
    persistState: { type: Boolean, default: true }, // Whether to persist state in sessionStorage
  };

  _applyOpenVisuals(index) {
    if (!this.itemTargets[index] || !this.triggerTargets[index] || !this.contentTargets[index]) {
      return;
    }

    const item = this.itemTargets[index];
    const trigger = this.triggerTargets[index];
    const content = this.contentTargets[index];
    const icon = this.hasIconTarget && this.iconTargets[index] ? this.iconTargets[index] : null;

    item.dataset.state = "open";
    trigger.setAttribute("aria-expanded", "true");
    trigger.dataset.state = "open";
    content.dataset.state = "open";
    content.removeAttribute("hidden");
    
    // Remove any inline style overrides to let CSS classes take over
    content.style.removeProperty("padding-top");
    content.style.removeProperty("padding-bottom");
    content.style.removeProperty("max-height");

    if (icon) {
      const isPlusMinus = icon.querySelector("path[d*=\"M5 12h14\"]");
      const isLeftChevron = icon.classList.contains("-rotate-90");

      if (isPlusMinus) {
        // For plus/minus icons, CSS handles the animation
      } else if (isLeftChevron) {
        // For left chevrons
        icon.classList.add("rotate-0");
        icon.classList.remove("-rotate-90");
      } else {
        // For regular chevrons
        icon.classList.add("rotate-180");
      }
    }
  }

  _applyClosedVisuals(index) {
    if (!this.itemTargets[index] || !this.triggerTargets[index] || !this.contentTargets[index]) {
      return;
    }

    const item = this.itemTargets[index];
    const trigger = this.triggerTargets[index];
    const content = this.contentTargets[index];
    const icon = this.hasIconTarget && this.iconTargets[index] ? this.iconTargets[index] : null;

    item.dataset.state = "closed";
    trigger.setAttribute("aria-expanded", "false");
    trigger.dataset.state = "closed";
    content.dataset.state = "closed";
    
    // Set dimensions to 0 for complete collapse
    content.style.setProperty("padding-top", "0", "important");
    content.style.setProperty("padding-bottom", "0", "important");
    content.style.setProperty("max-height", "0", "important");
    
    // Hide it
    content.setAttribute("hidden", "");

    if (icon) {
      const isPlusMinus = icon.classList.contains("scale-0");
      const isLeftChevron = icon.classList.contains("rotate-0");

      if (isPlusMinus) {
        // For plus/minus icons, CSS handles the animation
      } else if (isLeftChevron) {
        // For left chevrons
        icon.classList.remove("rotate-0");
        icon.classList.add("-rotate-90");
      } else {
        // For regular chevrons
        icon.classList.remove("rotate-180");
      }
    }
  }

  connect() {
    this.addKeyboardListeners();
    this.activeIndices = new Set(); // Start with a clean slate for internal tracking

    // Store bound function references for proper cleanup
    this.boundHandleTriggerKeydown = this.handleTriggerKeydown.bind(this);
    this.boundHandleKeydown = this.handleKeydown.bind(this);

    // Ensure all trigger buttons are focusable and have keyboard listeners
    this.triggerTargets.forEach((trigger, _index) => {
      // Add tabindex for Safari compatibility
      if (!trigger.hasAttribute("tabindex")) {
        trigger.setAttribute("tabindex", "0");
      }

      // Add individual keydown listener for Safari compatibility
      trigger.addEventListener("keydown", this.boundHandleTriggerKeydown);
    });

    // Try to restore state from sessionStorage first (if persistence is enabled)
    const storedState = this.persistStateValue ? this.getStoredState() : null;
    const initiallyOpenIndexesFromDOM = [];
    const hasStoredState = storedState && Object.keys(storedState).length > 0;
    
    this.itemTargets.forEach((item, index) => {
      const folderId = this.getFolderId(index);
      
      // If we have stored state, use it exclusively (ignore DOM)
      if (hasStoredState) {
        if (storedState[folderId]) {
          initiallyOpenIndexesFromDOM.push(index);
        }
      } else {
        // Only check DOM state if we don't have stored state
        if (this.triggerTargets[index].getAttribute("aria-expanded") === "true") {
          initiallyOpenIndexesFromDOM.push(index);
        } else if (item.dataset.state === "open" && !initiallyOpenIndexesFromDOM.includes(index)) {
          // Fallback or additional check for items where data-state might be set but not aria-expanded
          initiallyOpenIndexesFromDOM.push(index);
        }
      }
    });

    if (!this.allowMultipleValue) {
      if (initiallyOpenIndexesFromDOM.length > 0) {
        const indexToKeepOpen = initiallyOpenIndexesFromDOM[0]; // Keep the first one
        this.activeIndices.add(indexToKeepOpen);
        this._applyOpenVisuals(indexToKeepOpen);

        for (let i = 0; i < this.itemTargets.length; i++) {
          if (i !== indexToKeepOpen) {
            this._applyClosedVisuals(i);
          }
        }
      } else {
        this.itemTargets.forEach((_, index) => this._applyClosedVisuals(index));
      }
    } else {
      // Allow multiple
      initiallyOpenIndexesFromDOM.forEach((index) => {
        this.activeIndices.add(index);
        this._applyOpenVisuals(index);
      });
      // Ensure items not in initiallyOpenIndexesFromDOM are closed
      this.itemTargets.forEach((_, index) => {
        if (!this.activeIndices.has(index)) {
          this._applyClosedVisuals(index);
        }
      });
    }
    
    // Save initial state if we don't have stored state yet (and persistence is enabled)
    if (!hasStoredState && this.persistStateValue) {
      this.saveState();
    }
  }

  disconnect() {
    this.element.removeEventListener("keydown", this.boundHandleKeydown);

    // Remove individual trigger listeners
    this.triggerTargets.forEach((trigger) => {
      trigger.removeEventListener("keydown", this.boundHandleTriggerKeydown);
    });
  }

  addKeyboardListeners() {
    this.element.addEventListener("keydown", this.boundHandleKeydown);
  }

  // Safari-compatible trigger-specific keydown handler
  handleTriggerKeydown(event) {
    const currentTrigger = event.currentTarget;
    const currentIndex = this.triggerTargets.indexOf(currentTrigger);

    if (currentIndex === -1) {
      return;
    }

    switch (event.key) {
    case "ArrowUp":
      event.preventDefault();
      this.focusPreviousItem(currentIndex);
      break;
    case "ArrowDown":
      event.preventDefault();
      this.focusNextItem(currentIndex);
      break;
    case "Home":
      event.preventDefault();
      this.focusFirstItem();
      break;
    case "End":
      event.preventDefault();
      this.focusLastItem();
      break;
    case "Enter":
    case " ": // Space key
      event.preventDefault();
      this.toggle(event);
      break;
    }
  }

  handleKeydown(event) {
    // Fallback handler - try to find the focused trigger
    let currentIndex = -1;

    // Check if any trigger is focused
    this.triggerTargets.forEach((trigger, index) => {
      if (trigger === document.activeElement || trigger.contains(document.activeElement)) {
        currentIndex = index;
      }
    });

    if (currentIndex === -1) {
      return;
    }

    switch (event.key) {
    case "ArrowUp":
      event.preventDefault();
      this.focusPreviousItem(currentIndex);
      break;
    case "ArrowDown":
      event.preventDefault();
      this.focusNextItem(currentIndex);
      break;
    case "Home":
      event.preventDefault();
      this.focusFirstItem();
      break;
    case "End":
      event.preventDefault();
      this.focusLastItem();
      break;
    }
  }

  focusPreviousItem(currentIndex) {
    const previousIndex = (currentIndex - 1 + this.triggerTargets.length) % this.triggerTargets.length;
    this.triggerTargets[previousIndex].focus();
  }

  focusNextItem(currentIndex) {
    const nextIndex = (currentIndex + 1) % this.triggerTargets.length;
    this.triggerTargets[nextIndex].focus();
  }

  focusFirstItem() {
    this.triggerTargets[0].focus();
  }

  focusLastItem() {
    this.triggerTargets[this.triggerTargets.length - 1].focus();
  }

  toggle(event) {
    const index = this.triggerTargets.indexOf(event.currentTarget);

    if (this.activeIndices.has(index)) {
      this.close(index);
    } else {
      if (!this.allowMultipleValue) {
        this.activeIndices.forEach((i) => this.close(i));
      }
      this.open(index);
    }
    
    // Save state to sessionStorage (if persistence is enabled)
    if (this.persistStateValue) {
      this.saveState();
    }
  }

  open(index) {
    const content = this.contentTargets[index];
    const icon = this.iconTargets[index];

    // Add to activeIndices IMMEDIATELY (before animation)
    this.activeIndices.add(index);

    // Remove hidden and set initial state
    content.removeAttribute("hidden");
    content.style.maxHeight = "0px";

    // Force a reflow to ensure the initial state is applied
    content.offsetHeight;

    // Now set the final state
    requestAnimationFrame(() => {
      this.itemTargets[index].dataset.state = "open";
      this.triggerTargets[index].setAttribute("aria-expanded", "true");
      content.dataset.state = "open";

      // Handle different icon types
      if (icon) {
        const isPlusMinus = icon.querySelector("path[d*=\"M5 12h14\"]");
        const isLeftChevron = icon.classList.contains("-rotate-90");

        if (isPlusMinus) {
          // For plus/minus icons, we don't rotate, the CSS handles it
          this.triggerTargets[index].setAttribute("aria-expanded", "true");
        } else if (isLeftChevron) {
          // For left chevrons, add rotate class to go from -90 to 0
          icon.classList.add("rotate-0");
          icon.classList.remove("-rotate-90");
        } else {
          // For regular chevrons
          icon.classList.add("rotate-180");
        }
      }

      content.style.maxHeight = `${content.scrollHeight}px`;

      // Once the open animation completes, release the inline max-height so the
      // content can grow dynamically (e.g. rows added via Turbo while open).
      content.addEventListener("transitionend", () => {
        if (content.dataset.state === "open") {
          content.style.removeProperty("max-height");
        }
      }, { once: true });
    });
  }

  close(index) {
    const content = this.contentTargets[index];
    const icon = this.iconTargets[index];

    // Remove from activeIndices IMMEDIATELY (before animation)
    this.activeIndices.delete(index);

    this.itemTargets[index].dataset.state = "closed";
    this.triggerTargets[index].setAttribute("aria-expanded", "false");
    content.dataset.state = "closed";

    // Handle different icon types
    if (icon) {
      const isPlusMinus = icon.querySelector("path[d*=\"M5 12h14\"]");
      const isLeftChevron = icon.classList.contains("rotate-0");

      if (isPlusMinus) {
        // For plus/minus icons, CSS handles it
        this.triggerTargets[index].setAttribute("aria-expanded", "false");
      } else if (isLeftChevron) {
        // For left chevrons, go back to -90
        icon.classList.remove("rotate-0");
        icon.classList.add("-rotate-90");
      } else {
        // For regular chevrons
        icon.classList.remove("rotate-180");
      }
    }

    content.style.maxHeight = "0px";

    // Set a timeout fallback in case transition doesn't fire
    const hideContent = () => {
      if (content.dataset.state === "closed") {
        content.setAttribute("hidden", "");
        content.style.setProperty("padding-top", "0", "important");
        content.style.setProperty("padding-bottom", "0", "important");
      }
    };

    const timeoutId = setTimeout(hideContent, 350); // Slightly longer than 300ms transition

    // Wait for transition to complete
    content.addEventListener(
      "transitionend",
      () => {
        // Clear the timeout since transition completed
        clearTimeout(timeoutId);
        // Hide after any transition property finishes (max-height is most likely)
        if (content.dataset.state === "closed") {
          hideContent();
        }
      },
      { once: true },
    );
  }

  // Get a unique identifier for a folder based on its title
  getFolderId(index) {
    const trigger = this.triggerTargets[index];
    if (!trigger) {
      return `folder-${index}`;
    }
    
    // Try to get the text content from the trigger button
    const textContent = trigger.textContent.trim();
    // Convert to a slug-like format
    return textContent.toLowerCase().replace(/\s+/g, "-").replace(/[^\w-]/g, "");
  }

  // Save the current state to sessionStorage
  // Note: This should only be called when persistStateValue is true,
  // but we keep the check here as a safety measure
  saveState() {
    if (!this.persistStateValue) {
      return;
    }
    
    const state = {};
    
    this.itemTargets.forEach((item, index) => {
      const folderId = this.getFolderId(index);
      state[folderId] = this.activeIndices.has(index);
    });
    
    try {
      sessionStorage.setItem(this.storageKeyValue, JSON.stringify(state));
    } catch (error) {
      // Silently fail if sessionStorage is not available
      // eslint-disable-next-line no-console
      console.warn("Failed to save accordion state:", error);
    }
  }

  // Get the stored state from sessionStorage
  getStoredState() {
    try {
      const stored = sessionStorage.getItem(this.storageKeyValue);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      // Silently fail if sessionStorage is not available or JSON is invalid
      // eslint-disable-next-line no-console
      console.warn("Failed to load accordion state:", error);
      return null;
    }
  }
}
